import React, { useState } from 'react';
import { Input } from 'antd';
import { useQueryClient } from 'react-query';
import { Form, Formik } from 'formik';
import Modal from '../../../components/Modal';
import { useActions } from '../../../context/Actions.context';
import { Title } from '../../../components/styles';
import { useMuteAuthor } from '../../../core/modules/book/hooks/useMuteAuthor';
import { MUTEAUTHOR, SOURCE, UNMUTEAUTHOR } from '../../../core/constants';
import { ReactComponent as UnmuteIcon } from '../../../assets/icons/bell-off-fill-unmute.svg';
import { useUnmuteAuthor } from '../../../core/modules/book/hooks/useUnmuteAuthor';
import { useHistory } from 'react-router-dom';
import showActionNotification from '../../../section/Common/utils/showActionNotification';

const { TextArea } = Input;

interface IProps {
  selected_authors: string[];
  open: boolean;
  onClose: Function;
  onReset: Function;
  id: string;
}

const MuteSelectedAuthors: React.FC<IProps> = ({
  selected_authors,
  open,
  onClose,
  onReset,
  id
}) => {
  const { isActionLoading, setActionLoading, source: srcPath } = useActions();
  const { mutateAsync: muteAsync } = useMuteAuthor(MUTEAUTHOR);
  const { mutateAsync: unmuteAsync } = useUnmuteAuthor(UNMUTEAUTHOR);
  const history = useHistory();
  const queryClient = useQueryClient();

  const uniqueAuthors = Array.from(new Set(selected_authors));
  const [note, setNote] = useState('');

  const handleClose = (resetForm?: Function) => {
    setNote('');
    resetForm?.();
    onClose();
  };

  const onMuteAll = (_values, cb) => {
    if (isActionLoading) {
      return;
    }
    setActionLoading(true);

    try {
      const payload = {
        authors: uniqueAuthors,
        note,
        duration: {
          value: 6,
          unit: 'months'
        }
      };

      const promise = (muteAsync as Function)(payload);

      cb.resetForm();

      const capturedSource = srcPath['source'];
      const capturedAuthors = [...uniqueAuthors];

      promise.then(() => {
        showActionNotification({
          type: 'success',
          message: 'Authors muted successfully',
          destination: {
            name: 'Muted Authors',
            onGoTo: () => history.push(`${capturedSource}/muted-authors`)
          },
          onUndo: async () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (unmuteAsync as Function)({ authors: capturedAuthors, note });
          },
          queryClient,
          id
        });
      });

      promise.then(() => queryClient.refetchQueries(`get-${id}`));
      promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
      promise.then(() => handleClose(cb.resetForm));
      promise.finally(() => setActionLoading(false));
    } catch (error) {
      handleClose(cb.resetForm);
      onReset();

      showActionNotification({
        type: 'error',
        message: 'Failed to mute selected authors'
      });

      setActionLoading(false);

      console.error('Mute authors error', {
        selectedAuthors: uniqueAuthors,
        error
      });
    }
  };

  return (
    <Formik
      initialValues={{}}
      onSubmit={onMuteAll}
    >
      {({ handleSubmit, resetForm }) => (
        <Form>
          <Modal
            title='Mute Selected Author(s)'
            visible={open}
            onOk={handleSubmit}
            okText='Mute Authors'
            onCancel={() => handleClose(resetForm)}
            confirmLoading={isActionLoading}
            okButtonProps={{ disabled: isActionLoading }}
          >
            <Title>
              <p>
                Following <strong>{uniqueAuthors.length}</strong> author(s) will be muted for 6 months:
              </p>

              <br />

              <ul style={{ paddingLeft: 18 }}>
                {uniqueAuthors.slice(0, 5).map(a => (
                  <li key={a}>{a}</li>
                ))}
                {uniqueAuthors.length > 5 && (
                  <li key='more'>+ {uniqueAuthors.length - 5} more...</li>
                )}
              </ul>

              <br />

              <ul style={{ paddingLeft: 18 }}>
                <li>When muted, you won’t see any titles from these authors in
                  { srcPath['source'] === SOURCE.ao3 ? ' popular' : ' All New Titles or Hot Titles'}.</li>
                <li>All their titles will be available under <b>Muted Authors</b> tab.</li>
                <li>You can manually unmute anytime using {' '}
                  <UnmuteIcon
                    className='actions-icons actions-icons-unmute'
                    style={{ paddingTop: '5px' }}
                  /> <b>Unmute</b>.</li>
                <li>After 6 months, titles will reappear based on ranking.</li>
              </ul>
            </Title>

            <TextArea
              placeholder='Leave a note stating the reason behind muting author(s)'
              maxLength={400}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              style={{ marginTop: 12 }}
            />

            <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
              {note.length} / 400
            </div>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default React.memo(MuteSelectedAuthors);